import React, { ForwardedRef, forwardRef, useMemo, useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  YAxisProps,
  CartesianGrid,
  Cell,
} from 'recharts'
import { Box, Typography, useMediaQuery } from '@mui/material'
import styles from 'commonStyles/removeCartesianGrid.module.css'
import commonStyles from 'commonStyles/index.module.css'
import { GROUPING_LIST, INCOME_GROUP_INFO, PILLAR_INFO, REGION_INFO, SubIndicesData } from 'constant'
import { calculateStringWidth, isDataKeyExistsInData } from 'utils'

export interface BarChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[] | undefined
  isStackedBarChart: boolean
  xAxisTicks?: (string | number)[] | undefined
  yAxisTickRenderFunc?: YAxisProps['tick']
  CustomToolTipComponent?: any
  CustomLegendComponent?: (payload: any) => React.JSX.Element | undefined
  onClickBarChartEvent?: (data: any) => void
  is4Downloaded?: boolean
  configuration?: {
    currentGrouping?: string
  }
  subIndicesData: SubIndicesData | null
}

const SubIndicesChartComponent = forwardRef(
  (
    {
      data,
      isStackedBarChart,
      xAxisTicks,
      yAxisTickRenderFunc,
      CustomToolTipComponent,
      onClickBarChartEvent,
      is4Downloaded,
      subIndicesData,
    }: BarChartProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [hovered, setHovered] = useState(false)
    const yAxisWidth = useMemo(() => {
      if (!data || data.length === 0) return 0
      const countryNameWidths = data.map((item) =>
        calculateStringWidth(item.countryName, 'normal 16px "CircularStdMedium", sans-serif'),
      )
      const maxWidth = Math.max(...countryNameWidths)

      return maxWidth + 40
    }, [data])

    const calculateLegends = (dataParam: any[] | undefined) => {
      if (!dataParam) return undefined
      return PILLAR_INFO.filter((el) => subIndicesData?.pillarList.includes(el.dataKey)).map((pillar) => ({
        id: pillar.dataKey,
        type: 'square' as 'square',
        value: pillar.displayName,
        color: pillar.color,
      }))
    }

    const legendList = calculateLegends(data)
    const isSmallerThan1536 = useMediaQuery('(max-width:1536px)')
    const isSmallerThan1260 = useMediaQuery('(max-width:1260px)')

    const breakpoints = useMemo(() => {
      return {
        screen: isSmallerThan1536,
        grid: 168,
      }
    }, [isSmallerThan1536, isSmallerThan1260])
    return (
      <>
        <Box
          display={breakpoints.screen ? 'grid' : 'flex'}
          sx={{
            padding: '8px 0 16px 0',
            justifyContent: 'flex-start',
            gap: '20px',
            gridTemplateColumns: breakpoints.screen
              ? `repeat(auto-fill, minmax(${breakpoints.grid}px, 1fr))`
              : undefined,
          }}
        >
          {legendList?.map((l) => (
            <Box
              display={'flex'}
              flexDirection={'row'}
              flexWrap={'nowrap'}
              alignItems={'center'}
              gap={'6px'}
              key={l.id}
            >
              <Box sx={{ width: 14, height: 14, backgroundColor: l.color }}></Box>
              <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{l.value}</span>
            </Box>
          ))}
        </Box>
        <Box
          sx={{ overflowY: 'auto', overflowX: is4Downloaded ? 'hidden' : 'auto', mb: '16px' }}
          className={is4Downloaded ? undefined : commonStyles['scrollbar-common']}
          ref={ref}
        >
          <ResponsiveContainer minWidth='900px' width='100%' height={(data ?? []).length * 39} className={styles.grid}>
            <BarChart
              layout='vertical'
              data={data}
              margin={{
                top: 5,
                right: 24,
                left: 10,
                bottom: 5,
              }}
              barSize={20}
            >
              <XAxis
                type='number'
                tickLine={false}
                axisLine={false}
                domain={[0, 'dataMax + 2']}
                allowDataOverflow
                ticks={xAxisTicks}
              />
              <YAxis
                dataKey='name'
                tickLine={false}
                axisLine={false}
                type='category'
                tick={yAxisTickRenderFunc}
                width={yAxisWidth}
                fontFamily='CircularStdBook'
              />
              <CartesianGrid horizontal={false} />
              <Tooltip
                content={CustomToolTipComponent ? <CustomToolTipComponent show={true} /> : undefined}
                // labelFormatter={tooltipContent ? () => `${tooltipContent}` : undefined}
                // formatter={(value, name) => [parseFloat(value as string).toFixed(2), name]}
                cursor={{ fill: 'transparent' }}
              />
              {/* <Legend
              content={CustomLegendComponent ? CustomLegendComponent : undefined}
              layout='vertical'
              verticalAlign='bottom'
              align='right'
              formatter={(value) => <span style={{ color: 'black' }}>{value}</span>}
              payload={calculateLegends(currentGrouping, data)}
            /> */}
              {!isStackedBarChart && (
                <Bar
                  cursor='pointer'
                  dataKey='score'
                  fill='rgb(36, 148, 153)'
                  activeBar={{ fill: hovered ? 'rgb(36, 148, 153, 0.8)' : 'rgb(36, 148, 153)' }}
                  onMouseOver={() => {
                    setHovered(true)
                  }}
                  onMouseOut={() => {
                    setHovered(false)
                  }}
                  onClick={(dataOnClick) => {
                    onClickBarChartEvent && onClickBarChartEvent(dataOnClick)
                  }}
                  isAnimationActive={false}
                />
              )}
              {isStackedBarChart && (
                <>
                  {PILLAR_INFO.filter((el) => {
                    return isDataKeyExistsInData(data, el)
                  }).map((pillar) => (
                    <Bar
                      cursor='pointer'
                      key={pillar.dataKey}
                      dataKey={pillar.dataKey}
                      name={pillar.displayName}
                      fill={pillar.color}
                      activeBar={{ fill: hovered ? pillar.lightColor : pillar.color }}
                      stackId='a'
                      onMouseOver={() => {
                        setHovered(true)
                      }}
                      onMouseOut={() => {
                        setHovered(false)
                      }}
                      onClick={(dataOnClick) => {
                        onClickBarChartEvent && onClickBarChartEvent(dataOnClick)
                      }}
                      style={{ stroke: '#fff', strokeWidth: 1 }}
                      isAnimationActive={false}
                    />
                  ))}
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
          <Box sx={{ padding: '24px' }}>
            <Typography align='center' sx={{ fontSize: { xs: '12px', sm: '16px' } }}>
              Total Weighted Index Score
            </Typography>
          </Box>
        </Box>
      </>
    )
  },
)

SubIndicesChartComponent.displayName = 'SubIndicesChartComponent'

export default SubIndicesChartComponent
